import React from "react";
import { fallbackValues } from "./RadioButton.theme";
import { Motion } from "../layouts";
import styled from "styled-components";
import { themeComponent } from "../../../util/themeUtils";
import { useReducedMotion } from "framer-motion";

const HiddenRadioButton = styled.input`
  opacity: 0;
  position: absolute;
  cursor: ${({ disabled }) => (disabled ? "auto" : "pointer")};
`;

const RadioButton = ({
  radioOn = false,
  radioFocused = false,
  toggleRadio,
  name,
  disabled = false,
  ariaDescribedBy,
  themeValues,
  ariaLabelledBy = "",
  ariaLabel = null,
  isRequired = false,
  inputRef,
  tabIndex = "-1",
  checked = false,
  onFocus,
  onBlur,
  onKeyDown
}) => {
  const shouldReduceMotion = useReducedMotion();
  const reducedTransition = shouldReduceMotion ? { duration: 0 } : undefined;

  const buttonBorder = {
    onFocused: {
      borderColor: themeValues.activeColor,
      outlineWidth: "3px",
      outlineColor: themeValues.activeColor,
      outlineOffset: "2px"
    },
    offFocused: {
      borderColor: themeValues.activeColor,
      outlineWidth: "3px",
      outlineColor: themeValues.activeColor,
      outlineOffset: "2px"
    },
    on: {
      borderColor: themeValues.activeColor,
      outlineWidth: "0px",
      outlineColor: "rgba(0, 0, 0, 0)"
    },
    off: {
      borderColor: themeValues.inactiveColor,
      outlineWidth: "0px",
      outlineColor: "rgba(0, 0, 0, 0)"
    }
  };

  const buttonCenter = {
    on: {
      backgroundColor: themeValues.activeColor,
      height: "16px",
      width: "16px"
    },
    onFocused: {
      backgroundColor: themeValues.activeColor,
      height: "16px",
      width: "16px"
    },
    offFocused: {
      backgroundColor: "transparent",
      height: "0px",
      width: "0px"
    },
    off: {
      backgroundColor: "transparent",
      height: "0px",
      width: "0px"
    }
  };
  const extraProps = {};
  if (ariaLabelledBy && ariaLabelledBy.length) {
    extraProps["aria-labelledby"] = ariaLabelledBy;
  } else if (ariaLabel && ariaLabel !== null) {
    extraProps["aria-label"] = ariaLabel;
  }

  return (
    <Motion
      position="relative"
      padding="0"
      initial="off"
      animate={
        radioOn
          ? radioFocused
            ? "onFocused"
            : "on"
          : radioFocused
          ? "offFocused"
          : "off"
      }
    >
      <HiddenRadioButton
        type="radio"
        id={`radio-${name}`}
        ref={inputRef}
        disabled={disabled}
        checked={checked}
        onChange={toggleRadio}
        aria-describedby={ariaDescribedBy}
        tabIndex={tabIndex}
        required={isRequired}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        {...extraProps}
      />
      <Motion
        borderWidth="1px"
        borderStyle="solid"
        borderRadius="12px"
        margin="6px 8px 6px 6px"
        height="24px"
        width="24px"
        variants={buttonBorder}
        transition={reducedTransition}
        display="flex"
        extraStyles={`outline: 0px solid rgba(0, 0, 0, 0); justify-content: center; align-items: center;`}
      >
        <Motion
          variants={buttonCenter}
          transition={reducedTransition}
          borderRadius="8px"
        />
      </Motion>
    </Motion>
  );
};

export default themeComponent(RadioButton, "RadioButton", fallbackValues);
